/**
 * Leaderboard Component - Card-based list showing mining stats
 * Mobile-first design with two-column stats layout
 * Includes "Copy Settings" modal to copy another miner's settings
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { Facehash } from "facehash";
import { usePrivy } from "@privy-io/react-auth";
import { gameAPI, userAPI, accountsAPI } from "../api/client";
import { Trophy } from "lucide-react";
import "../win98.css";
/**React component imports */
import { Header } from "./Header";
import { TabButton } from "./TabButton";
import { Footer } from "./Footer";
import { useWatchlists } from "../hooks/useWatchlists";
import { TimeFilter } from "../types/watchlist";
import Spinner from "./Spinner";
import { useAccounts } from "../context/AccountContext";
import { getRandomLoadingMessage } from "../utils/loadingMessages";
import { useTranslation } from "../context/LanguageContext";
import { SEO } from "./SEO";

interface AccountBreakdown {
  address: string;
  name: string;
  isLegacy: boolean;
  solPnl: number;
  pnlUsd: number;
  roiPercent: number;
  oreMined: number;
  roundsPlayed: number | null;
  roundsWon: number | null;
}

interface LeaderboardEntry {
  wallet: string;
  roundsPlayed: number | null;
  roundsWon: number | null;
  topMinerWins: number | null;
  solPnl: number;
  solDeployed: number;
  oreMined: number;
  miningPnlUsd: number;
  roiPercent: number;
  lastSeen: string | null;
  accountsCount: number;
  accounts: AccountBreakdown[];
  badges: {
    isOG: boolean;
    hasSeeker: boolean;
    hasDiscord: boolean;
    hasMonke: boolean;
  };
  discord: {
    discordUserId: string;
    discordUsername: string | null;
    discordAvatar: string | null;
  } | null;
  privacyEnabled: boolean;
  copyDisabled: boolean;
  isOwnEntry?: boolean;
}

interface LeaderboardData {
  leaderboard: LeaderboardEntry[];
  prices: {
    ore_usd: number;
    sol_usd: number;
  };
  timestamp: string;
}

// Public settings that can be copied from another miner
interface PublicMiningSettings {
  deployment_mode: string;
  deployment_amount_sol: number;
  tiles_per_round: number;
  deploy_slot: number | null;
  mining_cost_timeframe: number;
  min_ev_percent: number | null;
  stop_loss_enabled: boolean;
  stop_loss_threshold: number | null;
  unlimited_rounds: boolean;
  max_rounds: number | null;
  motherlode_filters_enabled: boolean;
  motherlode_min: number | null;
  motherlode_max: number | null;
  motherlode_override_enabled: boolean;
  motherlode_override_threshold: number | null;
  motherlode_override_tiles: number | null;
}

type SortField = "miningPnlUsd" | "roundsPlayed" | "roundsWon" | "oreMined" | "roiPercent" | "topMinerWins";

interface LeaderboardProps {
  onClose?: () => void;
  onBackToHome?: () => void;
  userWalletAddress?: string;
  onSettingsApplied?: () => void; // Callback when settings are applied
}

// Animation variants for staggered panel entrance
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    }
  }
} as const;

const panelVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 25
    }
  }
};

// Format number with commas
function formatNumber(num: number | null | undefined, decimals: number = 1): string {
  if (num === null || num === undefined) return "0";
  if (num === 0) return "0";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Get color for PnL value (uses CSS variables for theme support)
function getPnlColor(value: number): string {
  if (value > 0) return "var(--win98-success)";
  if (value < 0) return "var(--win98-error)";
  return "var(--win98-text-primary)";
}

// Format wallet address: Abcd...1234
function formatWallet(wallet: string): string {
  if (!wallet || wallet.length < 8) return wallet;
  return `${wallet.slice(0, 4)}...${wallet.slice(-4)}`;
}

// Truncate address like HelloWindow.tsx: Abcde...12345
function truncateAddressSmall(address: string): string {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 5)}...${address.slice(-5)}`;
}


// Format currency
function formatCurrency(num: number): string {
  const prefix = num >= 0 ? "+$" : "-$";
  return `${prefix}${formatNumber(Math.abs(num), 2)}`;
}

// Helper to format deployment mode for display
function formatDeploymentMode(mode: string): string {
  switch (mode) {
    case "plus_mining_cost":
      return "Smart Deploy";
    case "plus_ev":
      return "+EV";
    case "random":
      return "Random";
    case "round_sniper":
      return "Round Sniper";
    case "sniper_pro":
      return "Sniper Pro";
    case "cheapest_tiles":
      return "Cheap Tile Hunter";
    default:
      return mode;
  }
}

// Deterministic facehash helpers using wallet character codes
function shouldBlink(wallet: string): boolean {
  return wallet.charCodeAt(2) % 10 === 0;
}

function shouldShowMouth(wallet: string): boolean {
  return wallet.charCodeAt(3) % 10 === 0;
}

const FACEHASH_PALETTES = [
  ["#264653", "#2a9d8f", "#e9c46a", "#f4a261", "#e76f51"],
  ["#606c38", "#283618", "#fefae0", "#dda15e", "#bc6c25"],
  ["#003049", "#d62828", "#f77f00", "#fcbf49", "#eae2b7"],
  ["#5f0f40", "#9a031e", "#fb8b24", "#e36414", "#0f4c5c"],
  ["#2b2d42", "#8d99ae", "#edf2f4", "#ef233c", "#d90429"],
  ["#10002b", "#240046", "#3c096c", "#5a189a", "#7b2cbf"],
];

function getWalletColors(wallet: string): string[] {
  return FACEHASH_PALETTES[wallet.charCodeAt(0) % FACEHASH_PALETTES.length];
}

// Mouth renderer for facehash (simple ellipsis animation)
const FaceMouth: React.FC = () => (
  <span style={{ fontSize: "10px", letterSpacing: "1px" }}>...</span>
);

// Format relative time for "Last Updated"
function formatLastUpdated(isoString: string | null, t: (key: string, vars?: Record<string, string>) => string): string {
  if (!isoString) return t('leaderboard.never');

  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('leaderboard.justNow');
  if (diffMins < 60) return t('leaderboard.minutesAgo', { count: String(diffMins) });
  if (diffHours < 24) return t('leaderboard.hoursAgo', { count: String(diffHours) });
  if (diffDays < 7) return t('leaderboard.daysAgo', { count: String(diffDays) });

  // For older dates, show the date
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Badge component for leaderboard entries
const Badge: React.FC<{ type: 'og' | 'seeker' | 'discord' | 'monke'; label: string }> = ({ type, label }) => {
  const colors = {
    og: { bg: '#F59E0B', text: 'black' },      // Gold
    seeker: { bg: '#7C3AED', text: 'white' },  // Purple
    discord: { bg: '#5865F2', text: 'white' }, // Discord blue
    monke: { bg: '#8B4513', text: 'white' },   // Brown (Saga Monke)
  };
  const color = colors[type];

  return (
    <span
      style={{
        fontSize: '9px',
        padding: '1px 4px',
        borderRadius: '2px',
        fontWeight: 'bold',
        backgroundColor: color.bg,
        color: color.text,
        marginLeft: '4px',
      }}
    >
      {label}
    </span>
  );
};

// Single Leaderboard Entry Component
const LeaderboardEntryCard = React.forwardRef<
  HTMLDivElement,
  {
    rank: number;
    entry: LeaderboardEntry;
    isCurrentUser?: boolean;
    isWatched?: boolean;
    isExpanded?: boolean;
    onAddressClick?: (address: string) => void;
    onCardClick?: (wallet: string, entry: LeaderboardEntry) => void;
    onToggleWatchlist?: (wallet: string) => void;
    onToggleExpand?: () => void;
  }
>(({ rank, entry, isCurrentUser, isWatched, isExpanded, onAddressClick, onCardClick, onToggleWatchlist, onToggleExpand }, ref) => {
  const { t } = useTranslation();
  const [addressCopied, setAddressCopied] = useState(false);

  const handleAddressClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger card click when clicking address
    if (onAddressClick) {
      onAddressClick(entry.wallet);
    } else {
      // Default: open in Solscan
      window.open(`https://solscan.io/account/${entry.wallet}`, "_blank");
    }
  };

  const handleCopyAddress = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger card click
    navigator.clipboard.writeText(entry.wallet);
    setAddressCopied(true);
    setTimeout(() => setAddressCopied(false), 2000);
  };

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(entry.wallet, entry);
    }
  };

  const handleWatchlistClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger card click
    if (onToggleWatchlist) {
      onToggleWatchlist(entry.wallet);
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  const hasMultipleAccounts = entry.accountsCount > 1;

  return (
    <div
      ref={ref}
      onClick={handleCardClick}
      style={{
        padding: "8px",
        borderBottom: "1px solid var(--win98-gray)",
        backgroundColor: isCurrentUser
          ? "var(--win98-notepad-bg)"
          : isWatched
          ? "var(--win98-selection-bg)"
          : rank % 2 === 0
          ? "var(--win98-lightgray)"
          : "var(--win98-white)",
        border: isCurrentUser ? "2px solid var(--win98-blue)" : isWatched ? "" : undefined,
        cursor: onCardClick ? "pointer" : "default",
      }}
      title={onCardClick ? t('leaderboard.copySettings') : undefined}
    >
      {/* Header: Rank + Address + Badges + Copy Button */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "4px",
          flexWrap: "wrap",
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      >
        {rank <= 3 ? (
          <>
            <Trophy size={12} color={rank === 1 ? "var(--win98-gold)" : rank === 2 ? "var(--win98-silver)" : "var(--win98-bronze)"} />
            <span className={`win98-rank-badge win98-text-reversed ${rank === 1 ? "gold" : rank === 2 ? "silver" : "bronze"}`}>
              #{rank}
            </span>
          </>
        ) : (
          <span className="win98-leaderboard-rank" style={{ minWidth: "24px" }}>
            #{rank}
          </span>
        )}
        {/* Discord avatar + username (only when username is resolved, visible, and not privacy-hidden) */}
        {entry.discord?.discordUsername && !entry.privacyEnabled && (
          <>
            {entry.discord.discordAvatar ? (
              <img
                src={`https://cdn.discordapp.com/avatars/${entry.discord.discordUserId}/${entry.discord.discordAvatar}.png?size=64`}
                alt=""
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  flexShrink: 0,
                }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <span style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                backgroundColor: "#5865F2",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                color: "#fff",
                fontWeight: 700,
                flexShrink: 0,
              }}>
                {entry.discord.discordUsername[0].toUpperCase()}
              </span>
            )}
            <span
              className="win98-leaderboard-wallet"
              style={{
                maxWidth: "100px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {entry.discord.discordUsername}
            </span>
          </>
        )}
        {/* Show facehash avatar + wallet address if no resolved Discord profile or privacy enabled */}
        {(!entry.discord?.discordUsername || entry.privacyEnabled) && (
          <>
            <span style={{ width: "30px", height: "30px", borderRadius: "50%", overflow: "hidden", flexShrink: 0, display: "inline-flex" }}>
              <Facehash
                name={entry.wallet.slice(0, 6)}
                size={38}
                intensity3d="dramatic"
                colors={getWalletColors(entry.wallet)}
                enableBlink={shouldBlink(entry.wallet)}
                onRenderMouth={shouldShowMouth(entry.wallet) ? () => <FaceMouth /> : undefined}
                showInitial={false}
              />
            </span>
            <span
              className="win98-leaderboard-wallet"
              onClick={handleAddressClick}
              title={t('leaderboard.viewOnSolscan')}
            >
              {truncateAddressSmall(entry.wallet)}
            </span>
          </>
        )}
        {/* Copy Address Button */}
        <span
          onClick={handleCopyAddress}
          style={{
            cursor: "pointer",
            fontSize: "12px",
            color: addressCopied ? "var(--win98-success)" : "var(--win98-text-primary)",
          }}
          title={addressCopied ? t('leaderboard.copied') : t('leaderboard.copyAddress')}
        >
          <i className={addressCopied ? "fa fa-check" : "fa fa-copy"} />
        </span>
        {isCurrentUser && (
          <span className="win98-leaderboard-label" style={{ color: "var(--win98-blue)", fontWeight: 700 }}>
            {t('leaderboard.you')}
          </span>
        )}
        {/* Badges + Star pushed to the right */}
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "4px" }}>
          {entry.badges?.isOG && <Badge type="og" label="OG" />}
          {entry.badges?.hasSeeker && <Badge type="seeker" label="SKR" />}
          {entry.badges?.hasMonke && <Badge type="monke" label="MNKE" />}
          {entry.badges?.hasDiscord && <Badge type="discord" label="DC" />}
          <span
            onClick={handleWatchlistClick}
            style={{
              cursor: "pointer",
              fontSize: "18px",
              color: "var(--win98-gold)",
            }}
            title={isWatched ? t('leaderboard.removeFromWatchlist') : t('leaderboard.addToWatchlist')}
          >
            {isWatched ? "★" : "☆"}
          </span>
        </span>
      </div>
      {/* Accounts count below header */}
      {hasMultipleAccounts && (
        <div
          onClick={handleExpandClick}
          style={{
            cursor: "pointer",
            fontSize: "11px",
            color: "var(--win98-text-secondary)",
            display: "flex",
            alignItems: "center",
            gap: "2px",
            paddingLeft: "8px",
            marginBottom: "2px",
          }}
          title={isExpanded ? t('leaderboard.collapseAccounts') : t('leaderboard.expandAccounts')}
        >
          ({entry.accountsCount} {t('leaderboard.accounts')})
          <i className={isExpanded ? "fa fa-chevron-up" : "fa fa-chevron-down"} style={{ fontSize: "10px" }} />
        </div>
      )}

      {/* First row: PNL + ROI */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      >
        <div style={{ display: "flex", gap: "4px", alignItems: "baseline" }}>
          <span className="win98-leaderboard-label">{t('leaderboard.pnl')}</span>
          {entry.privacyEnabled ? (
            <span className="win98-leaderboard-value" style={{ fontStyle: 'italic' }}>
              {t('leaderboard.hidden')}
            </span>
          ) : (
            <span
              className="win98-leaderboard-value"
              style={{
                color: getPnlColor(entry.miningPnlUsd),
                fontWeight: 700,
              }}
            >
              ${formatNumber(entry.miningPnlUsd, 2)}
            </span>
          )}
          <span
            className="win98-leaderboard-value"
            style={{
              color: getPnlColor(entry.roiPercent || 0),
              fontSize: '11px',
            }}
          >
            ({entry.roiPercent >= 0 ? '+' : ''}{formatNumber(entry.roiPercent || 0, 1)}%)
          </span>
        </div>
      </div>
      {/* Second row: Ore Mined + Last Updated */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingLeft: "8px",
          paddingRight: "8px",
          marginTop: "2px",
        }}
      >
        <div style={{ display: "flex", gap: "4px", alignItems: "baseline" }}>
          <span className="win98-leaderboard-label">{t('leaderboard.oreMined')}</span>
          <span className="win98-leaderboard-value">
            {entry.privacyEnabled ? t('leaderboard.hidden') : formatNumber(entry.oreMined, 1)}
          </span>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "baseline" }}>
          <span className="win98-leaderboard-label">
            {t('leaderboard.lastUpdated')}
          </span>
          <span className="win98-leaderboard-value">
            {formatLastUpdated(entry.lastSeen, t)}
          </span>
        </div>
        {entry.roundsPlayed !== null && (
          <div style={{ display: "flex", gap: "4px", alignItems: "baseline" }}>
            <span className="win98-leaderboard-label">
              {t('leaderboard.playedWon')}
            </span>
            <span className="win98-leaderboard-value">
              {formatNumber(entry.roundsPlayed, 0)}/
              {formatNumber(entry.roundsWon ?? 0, 0)}
            </span>
          </div>
        )}
      </div>

      {/* Third row: Top Miner Wins (only if available) */}
      {entry.topMinerWins !== null && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingLeft: "8px",
            paddingRight: "8px",
            marginTop: "2px",
          }}
        >
          <div style={{ display: "flex", gap: "4px", alignItems: "baseline" }}>
            <span className="win98-leaderboard-label">{t('leaderboard.topMinerWins')}</span>
            <span className="win98-leaderboard-value" style={{ color: entry.topMinerWins > 0 ? "var(--win98-gold)" : "var(--win98-text-primary)", fontWeight: entry.topMinerWins > 0 ? 700 : 400 }}>
              {formatNumber(entry.topMinerWins, 0)}
            </span>
          </div>
        </div>
      )}

      {/* Expanded accounts breakdown */}
      {isExpanded && hasMultipleAccounts && entry.accounts && (
        <div
          style={{
            marginTop: "8px",
            paddingTop: "8px",
            borderTop: "1px dashed var(--win98-gray)",
            paddingLeft: "16px",
            paddingRight: "8px",
          }}
        >
          {entry.accounts.map((acc, idx) => (
            <div
              key={acc.address}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                padding: "4px 0",
                borderBottom: idx < entry.accounts.length - 1 ? "1px dotted var(--win98-gray)" : undefined,
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                <span style={{ color: "var(--win98-text-secondary)", fontSize: "11px" }}>
                  {idx === 0 ? "└" : "├"}
                </span>
                <span
                  className="win98-leaderboard-wallet"
                  style={{ fontSize: "11px", cursor: onCardClick ? "pointer" : "default" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onCardClick) {
                      // Pass the subaccount address to fetch its specific settings
                      onCardClick(acc.address, entry);
                    }
                  }}
                  title={onCardClick ? t('leaderboard.copyAccountSettings') : acc.name}
                >
                  {acc.name}
                </span>
                {acc.isLegacy && (
                  <span style={{ fontSize: "9px", color: "var(--win98-text-secondary)" }}>{t('leaderboard.legacy')}</span>
                )}
              </div>
              <div style={{ display: "flex", gap: "12px", alignItems: "baseline" }}>
                <span
                  className="win98-leaderboard-value"
                  style={{
                    fontSize: "11px",
                    color: getPnlColor(acc.pnlUsd),
                    fontWeight: 600,
                  }}
                >
                  ${formatNumber(acc.pnlUsd, 2)}
                  <span style={{ fontSize: "10px", marginLeft: "2px" }}>
                    ({acc.roiPercent >= 0 ? '+' : ''}{formatNumber(acc.roiPercent, 1)}%)
                  </span>
                </span>
                <span className="win98-leaderboard-value" style={{ fontSize: "11px" }}>
                  {formatNumber(acc.oreMined, 1)} ORE
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

LeaderboardEntryCard.displayName = "LeaderboardEntryCard";

// Watchlist Entry Card - Similar to LeaderboardEntryCard but without rank and with remove button
const WatchlistEntryCard: React.FC<{
  wallet: string;
  entry?: LeaderboardEntry;
  index: number;
  onRemove: () => void;
  onCardClick?: () => void;
}> = ({ wallet, entry, index, onRemove, onCardClick }) => {
  const { t } = useTranslation();
  const [addressCopied, setAddressCopied] = useState(false);

  const handleAddressClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`https://solscan.io/account/${wallet}`, "_blank");
  };

  const handleCopyAddress = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(wallet);
    setAddressCopied(true);
    setTimeout(() => setAddressCopied(false), 2000);
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
  };

  // If wallet not found in leaderboard, show minimal info
  if (!entry) {
    return (
      <div
        style={{
          padding: "8px",
          borderBottom: "1px solid var(--win98-gray)",
          backgroundColor: index % 2 === 0 ? "var(--win98-lightgray)" : "var(--win98-white)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            paddingLeft: "8px",
            paddingRight: "8px",
          }}
        >
          <span style={{ width: "30px", height: "30px", borderRadius: "50%", overflow: "hidden", flexShrink: 0, display: "inline-flex" }}>
            <Facehash
              name={wallet.slice(0, 6)}
              size={38}
              intensity3d="dramatic"
              colors={getWalletColors(wallet)}
              enableBlink={shouldBlink(wallet)}
              onRenderMouth={shouldShowMouth(wallet) ? () => <FaceMouth /> : undefined}
              showInitial={false}
            />
          </span>
          <span
            className="win98-leaderboard-wallet"
            onClick={handleAddressClick}
            title={t('leaderboard.viewOnSolscan')}
          >
            {truncateAddressSmall(wallet)}
          </span>
          <span
            onClick={handleCopyAddress}
            style={{
              cursor: "pointer",
              fontSize: "12px",
              color: addressCopied ? "var(--win98-success)" : "var(--win98-text-primary)",
            }}
            title={addressCopied ? t('leaderboard.copied') : t('leaderboard.copyAddress')}
          >
            <i className={addressCopied ? "fa fa-check" : "fa fa-copy"} />
          </span>
          <span style={{ flex: 1 }} />
          <span
            onClick={handleRemoveClick}
            style={{
              cursor: "pointer",
              fontSize: "18px",
              color: "var(--win98-gold)",
            }}
            title={t('leaderboard.removeFromWatchlist')}
          >
            ★
          </span>
        </div>
        <div
          style={{
            paddingLeft: "8px",
            paddingRight: "8px",
            marginTop: "4px",
            color: "var(--win98-gray)",
            fontSize: "11px",
          }}
        >
          {t('leaderboard.notFound')}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onCardClick}
      style={{
        padding: "8px",
        borderBottom: "1px solid var(--win98-gray)",
        backgroundColor: index % 2 === 0 ? "var(--win98-lightgray)" : "var(--win98-white)",
        cursor: onCardClick ? "pointer" : "default",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "var(--win98-neutral-bg)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor =
          index % 2 === 0 ? "var(--win98-lightgray)" : "var(--win98-white)";
      }}
      title={onCardClick ? t('leaderboard.copySettings') : undefined}
    >
      {/* Header: Avatar + Address + Copy Button + Remove Star */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "4px",
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      >
        <span style={{ width: "30px", height: "30px", borderRadius: "50%", overflow: "hidden", flexShrink: 0, display: "inline-flex" }}>
          <Facehash
            name={wallet.slice(0, 6)}
            size={38}
            intensity3d="dramatic"
            colors={getWalletColors(wallet)}
            enableBlink={shouldBlink(wallet)}
            onRenderMouth={shouldShowMouth(wallet) ? () => <FaceMouth /> : undefined}
            showInitial={false}
          />
        </span>
        <span
          className="win98-leaderboard-wallet"
          onClick={handleAddressClick}
          title={t('leaderboard.viewOnSolscan')}
        >
          {truncateAddressSmall(wallet)}
        </span>
        <span
          onClick={handleCopyAddress}
          style={{
            cursor: "pointer",
            fontSize: "12px",
            color: addressCopied ? "var(--win98-success)" : "var(--win98-text-primary)",
          }}
          title={addressCopied ? t('leaderboard.copied') : t('leaderboard.copyAddress')}
        >
          <i className={addressCopied ? "fa fa-check" : "fa fa-copy"} />
        </span>
        <span style={{ flex: 1 }} />
        <span
          onClick={handleRemoveClick}
          style={{
            cursor: "pointer",
            fontSize: "18px",
            color: "var(--win98-gold)",
          }}
          title={t('leaderboard.removeFromWatchlist')}
        >
          ★
        </span>
      </div>

      {/* First row: Last Updated and PNL */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      >
        <div style={{ display: "flex", gap: "4px", alignItems: "baseline" }}>
          <span className="win98-leaderboard-label">{t('leaderboard.lastUpdated')}</span>
          <span className="win98-leaderboard-value">
            {formatLastUpdated(entry.lastSeen, t)}
          </span>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "baseline" }}>
          <span className="win98-leaderboard-label">{t('leaderboard.pnl')}</span>
          <span
            className="win98-leaderboard-value"
            style={{
              color: getPnlColor(entry.miningPnlUsd),
              fontWeight: 700,
            }}
          >
            ${formatNumber(entry.miningPnlUsd, 2)}
          </span>
        </div>
      </div>

      {/* Second row: Ore Mined and Played/Won (if available) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingLeft: "8px",
          paddingRight: "8px",
          marginTop: "2px",
        }}
      >
        <div style={{ display: "flex", gap: "4px", alignItems: "baseline" }}>
          <span className="win98-leaderboard-label">{t('leaderboard.oreMined')}</span>
          <span className="win98-leaderboard-value">
            {formatNumber(entry.oreMined, 1)}
          </span>
        </div>
        {entry.roundsPlayed !== null && (
          <div style={{ display: "flex", gap: "4px", alignItems: "baseline" }}>
            <span className="win98-leaderboard-label">{t('leaderboard.playedWon')}</span>
            <span className="win98-leaderboard-value">
              {formatNumber(entry.roundsPlayed, 0)}/{formatNumber(entry.roundsWon ?? 0, 0)}
            </span>
          </div>
        )}
      </div>

      {/* Third row: Top Miner Wins (only if available) */}
      {entry.topMinerWins !== null && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            paddingLeft: "8px",
            paddingRight: "8px",
            marginTop: "2px",
          }}
        >
          <div style={{ display: "flex", gap: "4px", alignItems: "baseline" }}>
            <span className="win98-leaderboard-label">{t('leaderboard.topMinerWins')}</span>
            <span
              className="win98-leaderboard-value"
              style={{
                color: entry.topMinerWins > 0 ? "var(--win98-gold)" : "var(--win98-text-primary)",
                fontWeight: entry.topMinerWins > 0 ? 700 : 400,
              }}
            >
              {formatNumber(entry.topMinerWins, 0)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const Leaderboard: React.FC<LeaderboardProps> = ({
  onClose,
  onBackToHome,
  userWalletAddress,
  onSettingsApplied,
}) => {
  const { authenticated, user, getAccessToken } = usePrivy();
  const { accounts } = useAccounts();
  const { t } = useTranslation();

  // Stable refs for functions used in intervals/effects to avoid stale closures
  const tRef = useRef(t);
  tRef.current = t;
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  // Filter to only subaccounts (exclude legacy) for copy settings target
  const subaccounts = useMemo(() => accounts.filter(a => !a.isLegacy), [accounts]);
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [sortField, setSortField] = useState<SortField>("miningPnlUsd");
  const listRef = useRef<HTMLDivElement>(null);
  const userEntryRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Copy Settings Modal state
  const [selectedWallet, setSelectedWallet] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<LeaderboardEntry | null>(
    null
  );
  const [selectedSettings, setSelectedSettings] =
    useState<PublicMiningSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [applyingSettings, setApplyingSettings] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);
  const [isOG, setIsOG] = useState(false);
  const [targetAccountId, setTargetAccountId] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");

  // Simplified single watchlist hook
  const {
    watchedWallets,
    activeTab: _activeTab,
    setActiveTab,
    toggleWallet,
    isWalletWatched,
    removeWallet,
  } = useWatchlists();

  // New filter states
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showDiscordOnly, setShowDiscordOnly] = useState(false);

  const [currentTab, setCurrentTab] = useState<'leaderboard' | 'watchlists'>('leaderboard');
  const loadingMessage = useMemo(() => getRandomLoadingMessage(), []);

  // Get user's wallet address from Privy if not provided
  const currentUserWallet = useMemo(() => {
    if (userWalletAddress) return userWalletAddress;
    if (!authenticated || !user) return null;

    const solanaWallet = user.linkedAccounts?.find(
      (account: any) =>
        account.type === "wallet" &&
        account.walletClientType === "privy" &&
        account.chainType === "solana"
    );
    return solanaWallet?.address || null;
  }, [userWalletAddress, authenticated, user]);

  // Get all user's miner addresses (legacy wallet + Evore oreMinerPdas)
  const userMinerAddresses = useMemo(() => {
    const addresses: string[] = [];

    // Add user's main wallet (legacy account miner)
    if (currentUserWallet) {
      addresses.push(currentUserWallet);
    }

    // Add all Evore subaccount miner addresses
    accounts.forEach(account => {
      if (account.oreMinerPda && !addresses.includes(account.oreMinerPda)) {
        addresses.push(account.oreMinerPda);
      }
    });

    return addresses;
  }, [currentUserWallet, accounts]);

  // Check if an address belongs to the current user
  const isUserAddress = useMemo(() => {
    return (address: string) => userMinerAddresses.includes(address);
  }, [userMinerAddresses]);

  // Expanded wallets state for showing per-account breakdown
  const [expandedWallets, setExpandedWallets] = useState<Set<string>>(new Set());

  const toggleExpand = (wallet: string) => {
    setExpandedWallets(prev => {
      const next = new Set(prev);
      if (next.has(wallet)) {
        next.delete(wallet);
      } else {
        next.add(wallet);
      }
      return next;
    });
  };

  const sortOptions = [
    { value: "miningPnlUsd", label: t('leaderboard.sortPnl') },
    { value: "roiPercent", label: t('leaderboard.sortRoi') },
    { value: "oreMined", label: t('leaderboard.sortOreMined') },
    { value: "roundsPlayed", label: t('leaderboard.sortPlayed') },
    { value: "roundsWon", label: t('leaderboard.sortWon') },
    { value: "topMinerWins", label: t('leaderboard.sortTopMiner') },
  ];

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const result = await gameAPI.getLeaderboard();
        setData(result);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch leaderboard:", err);
        setError(tRef.current('leaderboard.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();

    // Refresh every 60 seconds
    const interval = setInterval(fetchLeaderboard, 60000);
    return () => clearInterval(interval);
  }, [retryCount]);

  // Fetch OG status when authenticated
  useEffect(() => {
    const fetchOGStatus = async () => {
      if (!authenticated) {
        setIsOG(false);
        return;
      }
      try {
        const token = await getAccessTokenRef.current();
        if (!token) return;
        const settings = await userAPI.getSettings(token);
        setIsOG(settings.isOG || false);
      } catch (err) {
        console.error("Failed to fetch OG status:", err);
      }
    };
    fetchOGStatus();
  }, [authenticated]);



  // Handle star button click - directly toggle wallet in watchlist
  const handleWatchlistStarClick = (wallet: string) => {
    toggleWallet(wallet);
  };

  // Handle search
  const handleSearch = () => {
    setSearchQuery(searchInput.trim().toLowerCase());
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
  };

  // Sort and filter the leaderboard
  const sortedLeaderboard = useMemo(() => {
    if (!data?.leaderboard) return [];

    let filtered = [...data.leaderboard];

    // Apply time filter
    if (timeFilter !== 'all') {
      const now = new Date();
      let cutoffMs = 0;
      switch (timeFilter) {
        case '24h':
          cutoffMs = 24 * 60 * 60 * 1000;
          break;
        case '7d':
          cutoffMs = 7 * 24 * 60 * 60 * 1000;
          break;
        case '30d':
          cutoffMs = 30 * 24 * 60 * 60 * 1000;
          break;
      }
      filtered = filtered.filter(entry => {
        if (!entry.lastSeen) return false;
        const lastSeenDate = new Date(entry.lastSeen);
        return now.getTime() - lastSeenDate.getTime() <= cutoffMs;
      });
    }

    // Apply active in 24h filter
    if (showActiveOnly) {
      const now = new Date();
      const twentyFourHoursAgo = now.getTime() - 24 * 60 * 60 * 1000;
      filtered = filtered.filter(entry => {
        if (!entry.lastSeen) return false;
        const lastSeenDate = new Date(entry.lastSeen);
        return lastSeenDate.getTime() >= twentyFourHoursAgo;
      });
    }

    // Apply Discord-only filter
    if (showDiscordOnly) {
      filtered = filtered.filter(entry =>
        entry.discord?.discordUsername && !entry.privacyEnabled
      );
    }

    // Apply search filter (wallet address, Discord username, or Discord ID)
    if (searchQuery) {
      filtered = filtered.filter(entry =>
        entry.wallet.toLowerCase().includes(searchQuery) ||
        (entry.discord?.discordUsername?.toLowerCase().includes(searchQuery) ?? false) ||
        (entry.discord?.discordUserId?.toLowerCase().includes(searchQuery) ?? false)
      );
    }

    // Sort
    return filtered.sort((a, b) => {
      const aVal = (a[sortField] as number) ?? 0;
      const bVal = (b[sortField] as number) ?? 0;
      return bVal - aVal; // Always descending
    });
  }, [data?.leaderboard, sortField, searchQuery, timeFilter, showActiveOnly, showDiscordOnly]);

  // Check if any of user's accounts are in the leaderboard
  const userInLeaderboard = useMemo(() => {
    if (!userMinerAddresses.length || !sortedLeaderboard.length) return false;
    return sortedLeaderboard.some(
      (entry) => userMinerAddresses.includes(entry.wallet)
    );
  }, [userMinerAddresses, sortedLeaderboard]);

  // Calculate global stats
  const globalStats = useMemo(() => {
    if (!data?.leaderboard) return { totalSolDeployed: 0, totalOreMined: 0 };

    return data.leaderboard.reduce(
      (acc, entry) => ({
        totalSolDeployed: acc.totalSolDeployed + (entry.solDeployed || 0),
        totalOreMined: acc.totalOreMined + (entry.oreMined || 0),
      }),
      { totalSolDeployed: 0, totalOreMined: 0 }
    );
  }, [data?.leaderboard]);

  // Jump to user's first entry in the leaderboard
  const handleJumpToMe = () => {
    if (!userMinerAddresses.length) return;

    // Find first user address that has an entry
    for (const addr of userMinerAddresses) {
      const userEntry = userEntryRefs.current.get(addr);
      if (userEntry) {
        userEntry.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
  };

  // Store ref for user's entry
  const setEntryRef = (wallet: string, el: HTMLDivElement | null) => {
    if (el) {
      userEntryRefs.current.set(wallet, el);
    }
  };

  // Handle card click - fetch settings for that wallet
  const handleCardClick = async (wallet: string, entry: LeaderboardEntry) => {
    if (!authenticated) {
      setSettingsError(t('leaderboard.loginRequired'));
      setSelectedWallet(wallet);
      setSelectedEntry(entry);
      return;
    }

    setSelectedWallet(wallet);
    setSelectedEntry(entry);
    setSettingsLoading(true);
    setSettingsError(null);
    setSelectedSettings(null);

    // Auto-select if user has only one subaccount
    if (subaccounts.length === 1) {
      setTargetAccountId(subaccounts[0].id);
    } else {
      setTargetAccountId(null);
    }

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setSettingsError(t('leaderboard.failedToken'));
        setSettingsLoading(false);
        return;
      }

      const response = await gameAPI.getPublicSettings(wallet, accessToken);
      if (response.settings) {
        setSelectedSettings(response.settings);
      } else {
        setSettingsError(t('leaderboard.noSettingsFound'));
      }
    } catch (err) {
      console.error("Failed to fetch wallet settings:", err);
      setSettingsError(t('leaderboard.failedToLoadSettings'));
    } finally {
      setSettingsLoading(false);
    }
  };

  // Close settings modal
  const handleCloseSettingsModal = () => {
    setSelectedWallet(null);
    setSelectedEntry(null);
    setSelectedSettings(null);
    setSettingsError(null);
    setApplySuccess(false);
    setTargetAccountId(null);
  };

  // Apply settings to selected account
  const handleApplySettings = async () => {
    if (!selectedSettings || !authenticated) return;

    // Require at least one subaccount
    if (subaccounts.length === 0) {
      setSettingsError(t('leaderboard.noSubaccounts'));
      return;
    }

    // Require account selection if user has multiple subaccounts
    if (subaccounts.length > 1 && !targetAccountId) {
      setSettingsError(t('leaderboard.selectAccount'));
      return;
    }

    setApplyingSettings(true);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setSettingsError(t('leaderboard.failedToken'));
        setApplyingSettings(false);
        return;
      }

      const settingsPayload = {
        deployment_mode: selectedSettings.deployment_mode,
        deployment_amount_sol: selectedSettings.deployment_amount_sol,
        tiles_per_round: selectedSettings.tiles_per_round,
        deploy_slot: selectedSettings.deploy_slot,
        mining_cost_timeframe: selectedSettings.mining_cost_timeframe,
        min_ev_percent: selectedSettings.min_ev_percent,
        stop_loss_enabled: selectedSettings.stop_loss_enabled,
        stop_loss_threshold: selectedSettings.stop_loss_threshold,
        unlimited_rounds: selectedSettings.unlimited_rounds,
        max_rounds: selectedSettings.max_rounds,
        motherlode_filters_enabled: selectedSettings.motherlode_filters_enabled,
        motherlode_min: selectedSettings.motherlode_min,
        motherlode_max: selectedSettings.motherlode_max,
        motherlode_override_enabled: selectedSettings.motherlode_override_enabled,
        motherlode_override_threshold: selectedSettings.motherlode_override_threshold,
        motherlode_override_tiles: selectedSettings.motherlode_override_tiles,
      };

      // If user has multiple accounts, use accountsAPI to target specific account
      // Otherwise use userAPI for legacy/single account users
      if (targetAccountId) {
        await accountsAPI.updateSettings(accessToken, targetAccountId, settingsPayload);
      } else {
        await userAPI.updateSettings(accessToken, settingsPayload);
      }

      setApplySuccess(true);
      onSettingsApplied?.();

      // Close modal after showing success briefly
      setTimeout(() => {
        handleCloseSettingsModal();
      }, 1500);
    } catch (err) {
      console.error("Failed to apply settings:", err);
      setSettingsError(t('leaderboard.failedApply'));
    } finally {
      setApplyingSettings(false);
    }
  };

  // Show loading spinner while data is loading
  if (loading && !data) {
    return (
      <div className="win98-window" style={{ padding: '40px' }}>
        <div className="win98-content">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <Spinner />
            <p style={{ margin: 0 }}>{loadingMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="win98-landing-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
        <SEO
          title="Leaderboard — MineMore | Top ORE Miners"
          description="Live leaderboard of top ORE miners on Solana. Track mining performance, earnings, and ROI."
          path="/leaderboard"
        />
        {/* App Header */}
        <Header onBackToHome={onBackToHome} />

        {/* Leaderboard Window */}
        <motion.div variants={panelVariants}>
        <div className="win98-window" style={{ width: '100%', height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
        {/* Title Bar */}
        <div className="win98-title-bar">
          <div className="win98-title">
            <img src="/favicon.png" className="win98-title-icon" alt="" loading="lazy" />
            {t('leaderboard.title')}
          </div>
          <div className="win98-controls">
            <div className="win98-button-control">_</div>
            {onClose && (
              <div className="win98-button-control" onClick={onClose}>
                X
              </div>
            )}
          </div>
        </div>

        {/* Tabs Row */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            padding: "8px 8px 0",
            borderBottom: "2px solid var(--win98-gray)",
            background: "var(--win98-lightgray)",
          }}
        >
          <TabButton
            label={t('leaderboard.title')}
            icon="fa-trophy"
            active={currentTab === 'leaderboard'}
            onClick={() => { setCurrentTab('leaderboard'); setActiveTab('leaderboard'); }}
          />
          <TabButton
            label={`${t('leaderboard.watchlist')}${watchedWallets.length > 0 ? ` (${watchedWallets.length})` : ''}`}
            icon="fa-star"
            active={currentTab === 'watchlists'}
            onClick={() => { setCurrentTab('watchlists'); setActiveTab('watchlists'); }}
          />
        </div>

        {/* Content */}
        <div className="win98-content" style={{ padding: "8px", flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          {/* Watchlist Tab */}
          {currentTab === 'watchlists' && (
            <div style={{ padding: '8px 0', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {watchedWallets.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    background: 'var(--win98-white)',
                    border: '2px solid',
                    borderColor:
                      'var(--win98-button-shadow) var(--win98-button-light) var(--win98-button-light) var(--win98-button-shadow)',
                  }}
                >
                  <p style={{ fontSize: '12px', color: 'var(--win98-gray)', margin: 0 }}>
                    {t('leaderboard.watchlistEmpty')}
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    background: 'var(--win98-white)',
                    border: '2px solid',
                    borderColor:
                      'var(--win98-button-shadow) var(--win98-button-light) var(--win98-button-light) var(--win98-button-shadow)',
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                  }}
                >
                  {watchedWallets.map((wallet) => {
                    const entry = data?.leaderboard.find(e => e.wallet === wallet);
                    if (!entry) {
                      return (
                        <div
                          key={wallet}
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid var(--win98-gray)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            paddingLeft: '16px',
                            paddingRight: '16px',
                          }}
                        >
                          <span style={{ fontSize: '12px', color: 'var(--win98-gray)' }}>
                            {truncateAddressSmall(wallet)} — {t('leaderboard.notFound')}
                          </span>
                          <span
                            onClick={() => removeWallet(wallet)}
                            style={{ cursor: 'pointer', fontSize: '18px', color: 'var(--win98-gold)' }}
                            title={t('leaderboard.removeFromWatchlist')}
                          >
                            ★
                          </span>
                        </div>
                      );
                    }
                    const leaderboardRank = sortedLeaderboard.findIndex(e => e.wallet === wallet) + 1;
                    return (
                      <LeaderboardEntryCard
                        key={wallet}
                        rank={leaderboardRank || 0}
                        entry={entry}
                        isCurrentUser={isUserAddress(wallet)}
                        isWatched={true}
                        isExpanded={expandedWallets.has(wallet)}
                        onCardClick={handleCardClick}
                        onToggleWatchlist={handleWatchlistStarClick}
                        onToggleExpand={() => toggleExpand(wallet)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Leaderboard Tab */}
          {currentTab === 'leaderboard' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {error && (
                <div
                  className="win98-leaderboard-label"
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "var(--win98-error)",
                  }}
                >
                  <div>{error}</div>
                  <button
                    className="win98-button"
                    onClick={() => {
                      setError(null);
                      setRetryCount(c => c + 1);
                    }}
                    style={{ marginTop: "8px" }}
                  >
                    {t('leaderboard.retry')}
                  </button>
                </div>
              )}

              {data && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {/* Search Row */}
              <div
                style={{
                  marginBottom: "8px",
                  display: "flex",
                  gap: "4px",
                  alignItems: "flex-end",
                  flexWrap: "wrap",
                }}
              >
                {/* Search Input */}
                <div style={{ flex: 1, minWidth: "180px" }}>
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                    placeholder={t('leaderboard.searchPlaceholder')}
                    className="win98-input"
                    style={{
                      width: "100%",
                      height: "28px",
                      padding: "4px 6px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                {/* Search Button */}
                <button
                  className="win98-button"
                  onClick={handleSearch}
                  style={{
                    height: "28px",
                    width: "20px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title={t('leaderboard.search')}
                >
                  <i className="fa fa-search" />
                </button>

                {/* Clear Search Button */}
                {searchQuery && (
                  <button
                    className="win98-button"
                    onClick={handleClearSearch}
                    style={{
                      height: "28px",
                      padding: "0 8px",
                    }}
                    title={t('leaderboard.clearSearch')}
                  >
                    X
                  </button>
                )}
              </div>

                  {/* Global Stats */}
              <div
                style={{
                  marginBottom: "8px",
                  padding: "6px 8px",
                  backgroundColor: "var(--win98-lightgray)",
                  border: "1px solid var(--win98-gray)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                {/* Current Prices Row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "4px",
                  }}
                >
                  <span className="win98-leaderboard-text" style={{ fontWeight: 700 }}>{t('leaderboard.currentPrices')}</span>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <span className="win98-leaderboard-value">
                      <span className="win98-leaderboard-label">{t('leaderboard.solLabel')} </span>
                      <span style={{ fontWeight: 700 }}>
                        ${formatNumber(data.prices.sol_usd, 2)}
                      </span>
                    </span>
                    <span className="win98-leaderboard-value">
                      <span className="win98-leaderboard-label">{t('leaderboard.oreLabel')} </span>
                      <span style={{ fontWeight: 700 }}>
                        ${formatNumber(data.prices.ore_usd, 4)}
                      </span>
                    </span>
                  </div>
                </div>

                <div
                  style={{ borderTop: "1px solid var(--win98-gray)", paddingTop: "4px" }}
                >
                  <div className="win98-leaderboard-text" style={{ fontWeight: 700, marginBottom: "2px", paddingBottom: "4px" }}>
                    {t('leaderboard.minermoreUsers')}
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between", paddingBottom: "4px" }}
                  >
                    <span className="win98-leaderboard-value">
                      <span className="win98-leaderboard-label">
                        {t('leaderboard.totalSolDeployed')}{" "}
                      </span>
                      <span style={{ fontWeight: 700 }}>
                        {formatNumber(globalStats.totalSolDeployed, 2)} SOL
                      </span>
                    </span>
                    <span className="win98-leaderboard-value" style={{ color: "var(--win98-link)", fontWeight: 700 }}>
                      ($
                      {formatNumber(
                        globalStats.totalSolDeployed * data.prices.sol_usd,
                        2
                      )}
                      )
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between", paddingBottom: "4px" }}
                  >
                    <span className="win98-leaderboard-value">
                      <span className="win98-leaderboard-label">
                        {t('leaderboard.totalOreMined')}{" "}
                      </span>
                      <span style={{ fontWeight: 700 }}>
                        {formatNumber(globalStats.totalOreMined, 2)} ORE
                      </span>
                    </span>
                    <span className="win98-leaderboard-value" style={{ color: "var(--win98-link)", fontWeight: 700 }}>
                      ($
                      {formatNumber(
                        globalStats.totalOreMined * data.prices.ore_usd,
                        2
                      )}
                      )
                    </span>
                  </div>
                </div>
              </div>

              {/* Sort and Jump to Me Row */}
              <div
                style={{
                  marginBottom: "8px",
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {/* Sort Dropdown */}
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as SortField)}
                  className="win98-leaderboard-label"
                  style={{
                    background: "var(--win98-white)",
                    border: "2px solid",
                    borderColor: "var(--win98-button-shadow) var(--win98-button-light) var(--win98-button-light) var(--win98-button-shadow)",
                    boxShadow: "inset 1px 1px 0 var(--win98-button-dark-shadow)",
                    padding: "4px 6px",
                    fontFamily: '"MS Sans Serif", Tahoma, Arial, sans-serif',
                    height: "28px",
                    minWidth: "90px",
                    cursor: "pointer",
                    color: "var(--win98-text-primary)",
                  }}
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                {/* Jump to Me button - only show if user is logged in and in leaderboard */}
                {authenticated && userInLeaderboard && (
                  <button
                    className="win98-button win98-button-secondary"
                    onClick={handleJumpToMe}
                    style={{
                      height: "28px",
                      padding: "0 12px",
                    }}
                  >
                    {t('leaderboard.jumpToMe')}
                  </button>
                )}

                <button
                  className={`win98-button ${showDiscordOnly ? 'win98-button-primary' : 'win98-button-secondary'}`}
                  onClick={() => setShowDiscordOnly(prev => !prev)}
                  style={{
                    height: "28px",
                    padding: "0 12px",
                  }}
                >
                  <i className="fa fa-brands fa-discord" style={{ marginRight: "4px" }} />
                  {t('leaderboard.discordOnly')}
                </button>
              </div>

              {/* Leaderboard List */}
              <div
                ref={listRef}
                style={{
                  background: "var(--win98-white)",
                  border: "2px solid",
                  borderColor: "var(--win98-button-shadow) var(--win98-button-light) var(--win98-button-light) var(--win98-button-shadow)",
                  boxShadow: "inset 1px 1px 0 var(--win98-button-dark-shadow)",
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                }}
                role="list"
                aria-label="Mining leaderboard"
              >
                {sortedLeaderboard.length === 0 ? (
                  <div
                    className="win98-leaderboard-label"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                    }}
                  >
                    {t('leaderboard.empty')}
                  </div>
                ) : (
                  sortedLeaderboard.map((entry, index) => (
                    <LeaderboardEntryCard
                      key={entry.wallet}
                      ref={(el) => setEntryRef(entry.wallet, el)}
                      rank={index + 1}
                      entry={entry}
                      isCurrentUser={isUserAddress(entry.wallet)}
                      isWatched={isWalletWatched(entry.wallet)}
                      isExpanded={expandedWallets.has(entry.wallet)}
                      onCardClick={handleCardClick}
                      onToggleWatchlist={handleWatchlistStarClick}
                      onToggleExpand={() => toggleExpand(entry.wallet)}
                    />
                  ))
                )}
              </div>

              {/* Footer Info */}
              <div
                className="win98-helper-text"
                style={{
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>
                  {t('leaderboard.showingMiners', { count: String(sortedLeaderboard.length) })}
                </span>
                <span>
                  {t('leaderboard.updated')} {new Date(data.timestamp).toLocaleTimeString()}
                </span>
              </div>
            </div>
          )}
            </div>
          )}
        </div>
      </div>
      </motion.div>

      {/* Copy Settings Modal */}
      {selectedWallet && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleCloseSettingsModal}
        >
          <div
            className="win98-window"
            style={{ width: "400px", maxWidth: "90vw" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Title Bar */}
            <div className="win98-title-bar">
              <div className="win98-title">
                <img src="/favicon.png" className="win98-title-icon" alt="" loading="lazy" />
                {t('leaderboard.modalTitle', { wallet: formatWallet(selectedWallet) })}
              </div>
              <div className="win98-controls">
                <div
                  className="win98-button-control"
                  onClick={handleCloseSettingsModal}
                >
                  X
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="win98-content" style={{ padding: "16px" }}>
              {settingsLoading && (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  {t('leaderboard.loadingSettings')}
                </div>
              )}

              {settingsError && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "var(--win98-error)",
                  }}
                >
                  {settingsError === t('leaderboard.loginRequired') ? (
                    <>
                      {t('leaderboard.loginRequired').split(t('leaderboard.logIn'))[0]}
                      <span
                        onClick={() => {
                          handleCloseSettingsModal();
                          onBackToHome?.();
                        }}
                        style={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          color: "var(--win98-link)",
                        }}
                      >
                        {t('leaderboard.logIn')}
                      </span>
                      {' '}{t('leaderboard.toCopySettings')}
                    </>
                  ) : (
                    settingsError
                  )}
                </div>
              )}

              {selectedSettings && (
                <>
                  <div style={{ marginBottom: "16px" }}>
                    <p
                      className="win98-leaderboard-label"
                      style={{
                        marginBottom: "12px",
                      }}
                    >
                      {t('leaderboard.copySettingsDesc')}
                    </p>

                    <table
                      className="win98-leaderboard-label"
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                      }}
                    >
                      <tbody>
                        <tr style={{ backgroundColor: "var(--win98-lightgray)" }}>
                          <td style={settingLabelStyle}>{t('leaderboard.deployMode')}</td>
                          <td style={settingValueStyle}>
                            {formatDeploymentMode(
                              selectedSettings.deployment_mode
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td style={settingLabelStyle}>{t('leaderboard.deployAmount')}</td>
                          <td style={settingValueStyle}>
                            {(Number(selectedSettings.deployment_amount_sol) || 0).toFixed(4)}{" "}
                            SOL
                          </td>
                        </tr>
                        <tr style={{ backgroundColor: "var(--win98-lightgray)" }}>
                          <td style={settingLabelStyle}>{t('leaderboard.tilesPerRound')}</td>
                          <td style={settingValueStyle}>
                            {selectedSettings.tiles_per_round}
                          </td>
                        </tr>
                        {/* Deploy Slot - for slot-based modes */}
                        {(selectedSettings.deployment_mode === "round_sniper" ||
                          selectedSettings.deployment_mode === "sniper_pro" ||
                          selectedSettings.deployment_mode === "cheapest_tiles") &&
                          selectedSettings.deploy_slot !== null && (
                          <tr>
                            <td style={settingLabelStyle}>{t('leaderboard.deploySlot')}</td>
                            <td style={settingValueStyle}>
                              {t('leaderboard.slotsBeforeEnd', { count: String(selectedSettings.deploy_slot) })}
                            </td>
                          </tr>
                        )}
                        <tr style={{ backgroundColor: "var(--win98-lightgray)" }}>
                          <td style={settingLabelStyle}>{t('leaderboard.costTimeframe')}</td>
                          <td style={settingValueStyle}>
                            {t('leaderboard.rounds', { count: String(selectedSettings.mining_cost_timeframe) })}
                          </td>
                        </tr>
                        {selectedSettings.deployment_mode ===
                          "plus_mining_cost" &&
                          selectedSettings.min_ev_percent !== null && (
                            <tr>
                              <td style={settingLabelStyle}>{t('leaderboard.minEvFilter')}</td>
                              <td style={settingValueStyle}>
                                {selectedSettings.min_ev_percent}%
                              </td>
                            </tr>
                          )}
                        <tr style={{ backgroundColor: "var(--win98-lightgray)" }}>
                          <td style={settingLabelStyle}>{t('leaderboard.stopLoss')}</td>
                          <td style={settingValueStyle}>
                            {selectedSettings.stop_loss_enabled
                              ? `${
                                  selectedSettings.stop_loss_threshold?.toFixed(
                                    4
                                  ) || "0"
                                } SOL`
                              : t('leaderboard.disabled')}
                          </td>
                        </tr>
                        <tr>
                          <td style={settingLabelStyle}>{t('leaderboard.roundLimit')}</td>
                          <td style={settingValueStyle}>
                            {selectedSettings.unlimited_rounds
                              ? t('leaderboard.unlimited')
                              : t('leaderboard.rounds', { count: String(selectedSettings.max_rounds || 0) })}
                          </td>
                        </tr>
                        {selectedSettings.motherlode_filters_enabled && (
                          <tr style={{ backgroundColor: "var(--win98-lightgray)" }}>
                            <td style={settingLabelStyle}>{t('leaderboard.motherlodeFilter')}</td>
                            <td style={settingValueStyle}>
                              {selectedSettings.motherlode_min
                                ? t('leaderboard.minOre', { count: String(selectedSettings.motherlode_min) })
                                : ""}
                              {selectedSettings.motherlode_min &&
                              selectedSettings.motherlode_max
                                ? " | "
                                : ""}
                              {selectedSettings.motherlode_max
                                ? t('leaderboard.maxOre', { count: String(selectedSettings.motherlode_max) })
                                : ""}
                            </td>
                          </tr>
                        )}
                        {selectedSettings.motherlode_override_enabled && (
                          <tr>
                            <td style={settingLabelStyle}>{t('leaderboard.motherlodeOverride')}</td>
                            <td style={settingValueStyle}>
                              {selectedSettings.motherlode_override_threshold
                                ? `${selectedSettings.motherlode_override_threshold} ORE`
                                : "0 ORE"}{" "}
                              → {t('leaderboard.tiles', { count: String(selectedSettings.motherlode_override_tiles || 0) })}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Account Selector - always show target account info */}
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      className="win98-leaderboard-label"
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontWeight: "bold",
                      }}
                    >
                      {t('leaderboard.copyTo')}
                    </label>
                    {subaccounts.length === 0 ? (
                      /* No subaccounts - show message */
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "var(--win98-notepad-bg)",
                          border: "2px solid",
                          borderColor: "var(--win98-button-shadow) var(--win98-button-light) var(--win98-button-light) var(--win98-button-shadow)",
                          fontFamily: '"MS Sans Serif", Tahoma, Arial, sans-serif',
                          color: "var(--win98-text-secondary)",
                        }}
                      >
                        <i className="fa fa-info-circle" style={{ marginRight: "8px", color: "var(--win98-blue)" }} />
                        {t('leaderboard.noSubaccounts')}
                      </div>
                    ) : subaccounts.length === 1 ? (
                      /* Single subaccount - show which one it will copy to */
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "var(--win98-notepad-bg)",
                          border: "2px solid",
                          borderColor: "var(--win98-button-shadow) var(--win98-button-light) var(--win98-button-light) var(--win98-button-shadow)",
                          fontFamily: '"MS Sans Serif", Tahoma, Arial, sans-serif',
                          color: "var(--win98-text-primary)",
                        }}
                      >
                        <i className="fa fa-user" style={{ marginRight: "8px", color: "var(--win98-blue)" }} />
                        {subaccounts[0].displayName || `Account ${subaccounts[0].id.slice(0, 4)}`}
                        {subaccounts[0].isMining ? ` ${t('leaderboard.miningStatus')}` : ` ${t('leaderboard.stoppedStatus')}`}
                      </div>
                    ) : (
                      /* Multiple subaccounts - show dropdown */
                      <select
                        value={targetAccountId || ""}
                        onChange={(e) => setTargetAccountId(e.target.value || null)}
                        className="win98-leaderboard-label"
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          background: "var(--win98-white)",
                          border: "2px solid",
                          borderColor: "var(--win98-button-shadow) var(--win98-button-light) var(--win98-button-light) var(--win98-button-shadow)",
                          boxShadow: "inset 1px 1px 0 var(--win98-button-dark-shadow)",
                          fontFamily: '"MS Sans Serif", Tahoma, Arial, sans-serif',
                          cursor: "pointer",
                          color: "var(--win98-text-primary)",
                        }}
                      >
                        <option value="">{t('leaderboard.selectAccountOption')}</option>
                        {subaccounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.displayName || `Account ${acc.id.slice(0, 4)}`}
                            {acc.isMining ? ` ${t('leaderboard.miningStatus')}` : ` ${t('leaderboard.stoppedStatus')}`}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Buttons */}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      className="win98-button win98-button-secondary"
                      onClick={handleCloseSettingsModal}
                      style={{ padding: "4px 16px" }}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      className="win98-button win98-button-primary"
                      onClick={handleApplySettings}
                      disabled={applyingSettings || applySuccess || subaccounts.length === 0 || (subaccounts.length > 1 && !targetAccountId)}
                      style={{
                        padding: "4px 16px",
                        fontWeight: "bold",
                        backgroundColor: applySuccess ? "var(--win98-success)" : undefined,
                        color: applySuccess ? "var(--win98-white)" : undefined,
                      }}
                    >
                      {applySuccess
                        ? t('leaderboard.settingsCopied')
                        : applyingSettings
                        ? t('leaderboard.copying')
                        : subaccounts.length === 0
                        ? t('leaderboard.noSubaccountsBtn')
                        : subaccounts.length > 1 && !targetAccountId
                        ? t('leaderboard.selectAccountFirst')
                        : subaccounts.length === 1
                        ? t('leaderboard.copyToAccount', { name: subaccounts[0].displayName || subaccounts[0].id.slice(0, 4) })
                        : t('leaderboard.copyToAccount', { name: subaccounts.find(a => a.id === targetAccountId)?.displayName || t('leaderboard.selectedAccount') })}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </motion.div>
  );
};

// Setting row styles for modal (use CSS variables for theme support)
const settingLabelStyle: React.CSSProperties = {
  padding: "6px 8px",
  fontWeight: "bold",
  borderBottom: "1px solid var(--win98-gray)",
  color: "var(--win98-text-primary)",
};

const settingValueStyle: React.CSSProperties = {
  padding: "6px 8px",
  textAlign: "right",
  borderBottom: "1px solid var(--win98-gray)",
  color: "var(--win98-text-primary)",
};

export default Leaderboard;
